import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLanguage } from '../../contexts/LanguageContext';
import ProgramDay, { dayLabel, entryDomId } from './ProgramDay';
import ProgramPeople from './ProgramPeople';
import ProgramButterflies from './ProgramButterflies';
import { shownSponsors, SponsorPages } from './ProgramPages';
import { daysOf, indexEntries, isEntry, pick, PRINT_PAGE_MM, PrintContext } from './programModel';
import { LIVE_HASH, liveDay, useVenueNow } from './liveProgram';
import {
  AccentBar, DayTab, DayTabs, Deco, FilterBar, JumpButton, Notes, PrintDecoLayer, PrintScatter, Scatter, Section,
  SectionInner, SectionTitle, TextButton,
} from './programStyles';
import lungFlower from '../../assets/images/program/lungflower.webp';
import blueFlower from '../../assets/images/program/flower.webp';
import blossoms from '../../assets/images/program/blossoms.webp';
import heartRose from '../../assets/images/program/heartreal-1.webp';
import heartBlossom from '../../assets/images/program/heartreal-2.webp';
import heartBlue from '../../assets/images/program/heartblue-1.webp';
import heartBlueLight from '../../assets/images/program/heartblue-2.webp';

// Where "now" is in the shown day: the first running talk inside a block, else the first running
// block, else the now line (between slots).
const nowTarget = (section) => section?.querySelector(':not(article)[aria-current="time"]')
  || section?.querySelector('[aria-current="time"]')
  || section?.querySelector('[data-now-line]');

// On phones, on the day of the event: a small button at the bottom of the screen that scrolls to what
// runs now, shown while the program is on screen but "now" is not (the arrow says which way). Hidden on
// wider screens (the timeline shows where now is) and when nothing runs or "now" is past the last slot.
// Mounted while the shown day is live, on every screen (only the button itself is for phones).
const JumpToNow = ({ sectionRef, minutes }) => {
  const intl = useIntl();
  const [direction, setDirection] = useState(null); // 'up' | 'down' | null (hidden)
  // Come from another page's "Live" button (LiveProgramButton, the address ends in LIVE_HASH): straight
  // to what runs now, on any screen, once the program is shown; the anchor is then dropped.
  useEffect(() => {
    if (window.location.hash !== LIVE_HASH) return;
    const target = nowTarget(sectionRef.current);
    if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'center' });
    // once: coming back to this entry later (the back button) puts back where it was left instead
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  }, [sectionRef]);
  useEffect(() => {
    let frame = null;
    const check = () => {
      frame = null;
      const section = sectionRef.current;
      const target = nowTarget(section);
      if (!section || !target || typeof target.getBoundingClientRect !== 'function') {
        setDirection(null);
        return;
      }
      const view = window.innerHeight;
      const box = section.getBoundingClientRect();
      const at = target.getBoundingClientRect();
      const programShown = box.top < view && box.bottom > 0;
      if (!programShown || !box.height) setDirection(null);
      else if (at.bottom < 0) setDirection('up');
      else if (at.top > view) setDirection('down');
      else setDirection(null);
    };
    const schedule = () => { if (frame == null) frame = window.requestAnimationFrame(check); };
    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [sectionRef, minutes]);
  if (!direction) return null;
  const jump = () => {
    const target = nowTarget(sectionRef.current);
    if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  return (
    <JumpButton
      type="button"
      onClick={jump}
      aria-label={intl.formatMessage({ id: 'program.public.jumpToNow', defaultMessage: 'Go to what is happening now' })}
    >
      <span aria-hidden="true">{direction === 'up' ? '↑' : '↓'}</span>
      {intl.formatMessage({ id: 'program.public.running', defaultMessage: 'Happening now' })}
    </JumpButton>
  );
};

// The flowers down the band, in the crew page's style: the lung flower, its bouquet of blossoms
// (blossoms.webp, cut out of lungflower.png), the blue flower and two hearts painted from the lung
// flower's own texture into the anatomical heart of the HEARTS key visual (its outline and the light
// and shadow of its ventricles and vessels, taken from the cover video; heartreal-1.webp and
// heartreal-2.webp, two colourings) and the same heart filled with the blue flower's blossoms
// (heartblue-1.webp teal, heartblue-2.webp paler; made from flower.png and flower_1.png in that heart's
// outline, light and shadow), mirrored, turned and sized so the repetition does not show. `top` is
// relative to the whole section (timeline and people grid).
const SCATTER = [
  { src: blossoms, side: 'left', top: '8%', width: 300, offset: -50, opacity: 0.95, mirror: true, rotate: -6, phone: true },
  { src: blueFlower, side: 'right', top: '15%', width: 250, offset: -40, opacity: 0.75, rotate: 10 },
  { src: heartRose, side: 'left', top: '20%', width: 270, offset: -45, opacity: 0.85, rotate: -6, phone: true },
  { src: heartBlue, side: 'left', top: '28%', width: 230, offset: -45, opacity: 0.85, mirror: true, rotate: 7 },
  { src: blossoms, side: 'right', top: '29%', width: 280, offset: -30, opacity: 0.9, rotate: 4 },
  { src: heartBlueLight, side: 'right', top: '35%', width: 240, offset: -50, opacity: 0.85, rotate: -9 },
  { src: blueFlower, side: 'left', top: '36%', width: 230, offset: -50, opacity: 0.7, mirror: true, rotate: -14 },
  { src: heartBlossom, side: 'right', top: '42%', width: 260, offset: -45, opacity: 0.85, rotate: 10 },
  { src: lungFlower, side: 'left', top: '50%', width: 230, offset: -30, opacity: 0.8, mirror: true, rotate: -4, phone: true },
  { src: heartBlueLight, side: 'left', top: '57%', width: 250, offset: -45, opacity: 0.85, mirror: true, rotate: 5 },
  { src: blueFlower, side: 'right', top: '58%', width: 220, offset: -50, opacity: 0.7, rotate: 18 },
  { src: heartBlue, side: 'right', top: '64%', width: 245, offset: -40, opacity: 0.85, rotate: -7, phone: true },
  { src: blossoms, side: 'left', top: '65%', width: 270, offset: -40, opacity: 0.85, mirror: true, rotate: 6 },
  { src: heartRose, side: 'right', top: '71%', width: 265, offset: -45, opacity: 0.8, mirror: true, rotate: 8, phone: true },
  { src: blossoms, side: 'left', top: '80%', width: 250, offset: -40, opacity: 0.8, mirror: true, rotate: -8 },
  { src: heartBlue, side: 'right', top: '84%', width: 235, offset: -45, opacity: 0.85, mirror: true, rotate: 9 },
  { src: heartBlossom, side: 'left', top: '90%', width: 250, offset: -45, opacity: 0.8, rotate: -8 },
];

// The same flowers in the PDF: on every A4 page one at the left edge (over the time column) and one at
// the right edge, partly cut off like on the website, changing from page to page. Printed, an
// absolutely placed flower counts in printed pages of PRINT_PAGE_MM (ProgramPrint's A4 less its @page
// margins) from the top of its layer, so on page k of the layer it stands at k * PRINT_PAGE_MM + its
// place on the page, and every flower ends before its page does (no flower is split by a page break).
// The first page's right one starts below the lung flower. Only the pages the program fills get
// flowers (`printPages`, measured by ProgramPrintCopy): one placed further down would add an empty
// page. The pages after the sponsor pages (`printPagesAfterSponsors`) have a layer of their own that
// starts on the page after them (SponsorPages), as how many pages come before is not known in advance;
// the sponsor pages have none.
const PRINT_PAGES = 40;
// Left (even places) and right (odd places) of each page; every other page has a heart on both sides,
// a pink and a blue one.
const PRINT_ART = [
  heartRose, heartBlue, blossoms, blueFlower, heartBlueLight, heartBlossom, lungFlower,
  blossoms, heartBlossom, heartBlue, blueFlower, lungFlower, heartBlueLight, heartRose,
];
const PRINT_LEFT = [{ top: 150, rotate: -8 }, { top: 60, rotate: 6 }, { top: 120, rotate: -4 }];
const PRINT_RIGHT = [{ top: 170, rotate: 8 }, { top: 30, rotate: -6 }, { top: 110, rotate: 5 }];
// The flowers of `count` pages of a layer; `first` continues the variation of the pages before it.
const printScatter = (count, first = 0) => Array.from({ length: Math.min(count, PRINT_PAGES) }, (_, page) => {
  const k = first + page;
  const left = PRINT_LEFT[k % 3];
  const right = PRINT_RIGHT[k % 3];
  return [
    { src: PRINT_ART[(2 * k) % PRINT_ART.length], side: 'left', top: page * PRINT_PAGE_MM + left.top, width: 34, offset: -9,
      opacity: 0.8, mirror: k % 2 === 0, rotate: left.rotate },
    { src: PRINT_ART[(2 * k + 1) % PRINT_ART.length], side: 'right', top: page * PRINT_PAGE_MM + (k === 0 ? 120 : right.top),
      width: 40, offset: -11, opacity: 0.95, mirror: k % 2 === 1, rotate: right.rotate },
  ];
}).flat();

const PrintFlowers = ({ flowers }) => flowers.map((f, i) => (
  // eslint-disable-next-line react/no-array-index-key -- a fixed list
  <PrintScatter key={i} src={f.src} alt="" $side={f.side} $top={f.top} $width={f.width} $offset={f.offset}
    $opacity={f.opacity} $mirror={f.mirror} $rotate={f.rotate} />
));

const NONE = new Set();
const END_OF_DAY = 24 * 60;

// Whether the people grid shows anything (ProgramPeople renders nothing without people and members).
const hasPeople = (people) => (people?.people || []).length > 0
  || (people?.bodies || []).some((b) => (b.members || []).length > 0);

// The ids of every entry in which this person is named (labels carry no person id).
const entriesOf = (program, personId) => {
  const ids = new Set();
  const walk = (nodes) => (nodes || []).forEach((n) => {
    if (!isEntry(n)) return;
    if ((n.people || []).some((p) => !p.tba && p.personId === personId)) ids.add(n.id);
    walk(n.children);
  });
  if (personId != null) walk(program?.entries);
  return ids;
};

// The printed name of a person id, from the people list or the program.
const nameOf = (program, people, personId) => {
  const listed = (people?.people || []).find((p) => p.personId === personId);
  if (listed) return listed.name;
  let found = '';
  const walk = (nodes) => (nodes || []).forEach((n) => {
    if (found || !isEntry(n)) return;
    const p = (n.people || []).find((x) => !x.tba && x.personId === personId);
    if (p) found = p.name;
    else walk(n.children);
  });
  walk(program?.entries);
  return found;
};

// The public program section (P10, plan §7.6), from data: `program` is the answer of
// GET /api/public/events/{slug}/program, `people` the answer of .../people (null: no people grid).
// It fetches nothing, so the admin preview (P11, decision 7) can render it with its own data;
// PublicProgramSection is the thin fetching wrapper for the public page.
// Renders nothing for a program without entries and without a public PDF. A program the admin
// published with only its PDF (P10a) has an empty tree: the title, the notes and the download button
// are then the whole section - no day tabs, no timeline, nothing about "no entries".
// The sponsor pages (program.pages) stand where the admin placed them: BEFORE_PEOPLE (also the
// default) between the timeline and the people grid, AFTER_PEOPLE below the grid, AFTER_ENTRY right
// after the slot of that top-level entry (on its day; ProgramDay asks ctx.afterEntry). The print copy
// has them in the same place, so the PDF does too.
// print: the static version for the admin's PDF export (ProgramPrint): every day one after the other,
// names as text, no cards, buttons, tabs or decoration; `title` then heads the page.
const ProgramSectionView = ({
  program, people, language: languageProp, showTitle = true, print = false, printPages = 0, printPagesAfterSponsors = 0,
  title,
}) => {
  const intl = useIntl();
  const { language: siteLanguage } = useLanguage();
  const language = languageProp || siteLanguage;

  const days = useMemo(() => daysOf(program), [program]);
  const index = useMemo(() => indexEntries(program), [program]);
  // The venue's date and time, every minute (not in print): on a program day that is today the
  // running entries are marked and a line shows "now" (liveProgram.js), and that day opens first.
  const now = useVenueNow(program?.event?.timeZone, !print);
  // The day shown is kept in the browser's history entry (history.state.programDay), so that coming
  // back to the page (the back button after a speaker's profile) shows the same day again.
  const [dayIndex, setDayIndex] = useState(() => {
    const kept = print ? null : window.history.state?.programDay;
    if (Number.isInteger(kept) && kept >= 0 && kept < days.length) return kept;
    return now ? Math.max(days.findIndex((d) => d.day === now.day), 0) : 0;
  });
  const [selection, setSelection] = useState(null); // a person id
  const sectionRef = useRef(null);
  const highlighted = useMemo(() => entriesOf(program, selection), [program, selection]);

  const dayOfEntry = (id) => {
    const at = index.get(id);
    return at ? days.findIndex((d) => d.id === at.dayId) : -1;
  };

  const select = (personId) => {
    setSelection(personId);
    if (personId == null) return;
    const first = [...entriesOf(program, personId)].map(dayOfEntry).filter((i) => i >= 0).sort((a, b) => a - b)[0];
    if (first != null) setDayIndex(first);
  };

  useEffect(() => {
    if (print || window.history.state?.programDay === dayIndex) return;
    window.history.replaceState({ ...window.history.state, programDay: dayIndex }, '');
  }, [print, dayIndex]);

  // Jump to the first highlighted entry of the shown day once it is rendered.
  useEffect(() => {
    if (selection == null) return;
    const target = [...highlighted].find((id) => dayOfEntry(id) === dayIndex);
    const el = target != null ? document.getElementById(entryDomId(target)) : null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, dayIndex]);

  // The program PDF is not offered here: the public site has no PDF button (the admin exports it,
  // PublicPreviewModal), so a program without a public entry shows nothing.
  if (!program || days.length === 0) return null;

  const currentDay = days.length > 0 ? days[Math.min(dayIndex, days.length - 1)] : null;
  const daysWithSelection = new Set([...highlighted].map(dayOfEntry));
  const notes = pick(language, program.event?.programNotesDe, program.event?.programNotesEn);
  const sponsorsAt = shownSponsors(program, print).length > 0 ? program.pages.sponsorsPosition || 'BEFORE_PEOPLE' : null;
  const flowersAfterSponsors = print ? printScatter(printPagesAfterSponsors, printPages) : [];
  const sponsorPages = (breakAfter) => (
    <SponsorPages program={program} breakAfter={breakAfter} flowersAfter={breakAfter ? flowersAfterSponsors : []} />
  );
  const afterEntry = sponsorsAt === 'AFTER_ENTRY'
    ? (id) => (id === program.pages.sponsorsAfterEntryId ? sponsorPages(true) : null)
    : undefined;
  // Today live; an earlier day of the event while the event still runs (today is not after its last
  // day) is over as a whole: read at the day's end, every timed entry is past and nothing runs.
  const lastDay = days.map((d) => d.day).filter(Boolean).sort().pop();
  const dayOver = Boolean(now && currentDay?.day && currentDay.day < now.day && now.day <= lastDay);
  const live = now && currentDay?.day === now.day ? liveDay(program, currentDay, now.minutes)
    : dayOver ? liveDay(program, currentDay, END_OF_DAY) : null;
  const ctx = {
    highlighted, selection, onSelectPerson: print ? undefined : select, print, afterEntry,
    running: live ? live.running : NONE, past: live ? live.past : NONE, nowLine: live?.line ?? null,
    nowMinutes: live && !dayOver ? now.minutes : null,
    passedSlots: live ? live.passed : 0,
  };

  return (
    <PrintContext.Provider value={print}>
      <Section ref={sectionRef} aria-labelledby={showTitle ? 'public-program-title' : undefined}>
        <Deco $variant="lung" src={lungFlower} alt="" aria-hidden="true" />
        {!print && <ProgramButterflies flowers={SCATTER} />}
        {print && printPages > 0 && (
          <PrintDecoLayer aria-hidden="true">
            <PrintFlowers flowers={printScatter(printPages)} />
          </PrintDecoLayer>
        )}
        {!print && SCATTER.map((f, i) => (
          <Scatter
            // eslint-disable-next-line react/no-array-index-key -- a fixed list
            key={i}
            src={f.src}
            alt=""
            aria-hidden="true"
            loading="lazy"
            $side={f.side}
            $top={f.top}
            $width={f.width}
            $offset={f.offset}
            $opacity={f.opacity}
            $mirror={f.mirror}
            $rotate={f.rotate}
            $phone={f.phone}
          />
        ))}
        <SectionInner>
          {showTitle && (
            <>
              <SectionTitle id="public-program-title">
                {title || intl.formatMessage({ id: 'program.public.title', defaultMessage: 'Program' })}
              </SectionTitle>
              <AccentBar />
            </>
          )}
          {notes && <Notes>{notes}</Notes>}

          {days.length > 1 && !print && (
            <DayTabs role="tablist" aria-label={intl.formatMessage({ id: 'program.public.days', defaultMessage: 'Program days' })}>
              {days.map((d, i) => (
                <DayTab
                  key={d.id ?? 'other'}
                  type="button"
                  role="tab"
                  aria-selected={i === dayIndex}
                  $active={i === dayIndex}
                  onClick={() => setDayIndex(i)}
                >
                  {dayLabel(intl, language, d) || intl.formatMessage({ id: 'program.public.noDay', defaultMessage: 'More' })}
                  {selection != null && daysWithSelection.has(i) ? ' •' : ''}
                </DayTab>
              ))}
            </DayTabs>
          )}

          {selection != null && (
            <FilterBar role="status">
              <span>
                {intl.formatMessage(
                  { id: 'program.public.filter', defaultMessage: 'Appearances of {name} are highlighted.' },
                  { name: nameOf(program, people, selection) },
                )}
              </span>
              <TextButton type="button" onClick={() => setSelection(null)}>
                {intl.formatMessage({ id: 'program.public.filter.clear', defaultMessage: 'Clear' })}
              </TextButton>
            </FilterBar>
          )}

          {print
            ? days.map((d) => (
              <ProgramDay key={d.id ?? 'other'} program={program} day={d} language={language} ctx={ctx} showHeading />
            ))
            : currentDay && (
              <ProgramDay program={program} day={currentDay} language={language} ctx={ctx} showHeading={days.length === 1} />
            )}

          {sponsorsAt && sponsorsAt !== 'AFTER_PEOPLE' && sponsorsAt !== 'AFTER_ENTRY' && sponsorPages(hasPeople(people))}
          {people && <ProgramPeople people={people} selection={selection} onSelectPerson={print ? undefined : select} />}
          {sponsorsAt === 'AFTER_PEOPLE' && sponsorPages(false)}
        </SectionInner>
        {live && !dayOver && (live.running.size > 0 || live.line) && <JumpToNow sectionRef={sectionRef} minutes={now.minutes} />}
      </Section>
    </PrintContext.Provider>
  );
};

export default ProgramSectionView;
